import { authFetch } from './auth-fetch';
import type { ConversationParticipant, ConversationScope, ConversationStatus, Message } from './communication';
import type { Page } from './endpoints';
import type { RunSummary } from './runs';
import type { components } from './schema';

/**
 * The orchestration slice of the API: tasks (`12 §8`), approvals (`12 §9`) and
 * autonomy policies with their merge-gate simulator.
 *
 * Same rule as the other API files: no client-side modelling and no renaming.
 * The enums are read from the generated schema rather than spelled here, for the
 * reason `runs.ts` explains at length — a hand-written list compiled happily
 * while matching nothing at runtime.
 */

export type TaskStatus = components['schemas']['TaskSchema']['status'];
export type TaskType = components['schemas']['TaskSchema']['type'];
export type TaskPriority = components['schemas']['TaskSchema']['priority'];
export type RiskLevel = components['schemas']['TaskSchema']['risk_level'];
export type TaskEventType = components['schemas']['TaskEventSchema']['type'];
export type ApprovalSubjectType = components['schemas']['ApprovalSchema']['subject_type'];
export type ApprovalStatus = components['schemas']['ApprovalSchema']['status'];

/**
 * The kanban card: what `TaskView::card` carries and no more. The board is read
 * fifty at a time, so it has nothing that needs a join — the reasons and the
 * project arrive with the summary, which is a row somebody sorts.
 */
export interface TaskCard {
  id: string;
  number: number;
  type: TaskType;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  risk_level: RiskLevel;
  labels: string[];
  assignee: { id: string; name: string; avatar_url: string | null } | null;
  cost_micro_usd: number;
  budget_limit_micro_usd: number | null;
  due_at: string | null;
  /** La estantería (`12 §8`): fuera del tablero, no un estado. Solo la llevan tareas terminales. */
  archived_at: string | null;
  updated_at: string;
}

export interface Task extends TaskCard {
  project: { id: string; name: string; slug: string } | null;
  blocked_reason: string | null;
  /**
   * La mitad legible por máquina de `09 §7.3`. La frase de al lado es prosa y
   * nada debe ramificar sobre ella; esto es sobre lo que se ramifica.
   */
  blocked_reason_code: string | null;
  failure_reason: string | null;
  retry_count: number;
  /**
   * Si el equipo se reúne antes de planificar (`09 §2`). Solo se puede cambiar
   * mientras la tarea no haya arrancado; después, el `PATCH` responde 409.
   */
  team_meeting: boolean;
  /**
   * Si la tarea espera al despliegue de Jenkins del proyecto antes de darse
   * por hecha (`09 §2.2`). Encendido por defecto; solo actúa con un pipeline
   * asociado. Se puede cambiar hasta el merge; después, el `PATCH` responde 409.
   */
  await_deploy: boolean;
  pull_request_number: number | null;
  pull_request_url: string | null;
  created_at: string;
}

export interface TaskDetail extends Task {
  company_id: string;
  description: string;
  acceptance_criteria: string | null;
  parent_task: { id: string; number: number; title: string } | null;
  reviewer: { id: string; name: string } | null;
  qa: { id: string; name: string } | null;
  requested_by: { type: 'user' | 'employee' | 'webhook' | 'schedule' | 'system'; id: string | null };
  branch_name: string | null;
  external_issue_number: number | null;
  external_issue_url: string | null;
  repositories: {
    id: string;
    full_name: string;
    role: string | null;
    is_primary: boolean;
    default_branch: string;
  }[];
  blocked_by: { id: string; number: number; title: string; status: TaskStatus }[];
  pending_approvals: number;
  /** La compuerta de despliegue (`09 §2.2`): nula hasta que el merge la arma. */
  deploy: {
    merged_at?: string;
    status?: 'pending_request' | 'requested' | 'observing' | 'succeeded' | 'failed';
    attempts?: number;
    job_name?: string;
    autonomy?: string;
    operation_id?: string;
    build_number?: number;
  } | null;
  /**
   * Los repartos forzados de `09 §3.3.1`: una fase que hizo alguien cuyo rol de
   * proyecto no la cubre, porque nadie tenía el rol que la fase quería. Vacío en
   * el caso normal. Es una advertencia sobre el **valor** de esa revisión, no
   * sobre el estado de la tarea.
   */
  forced_staffing: {
    phase: string;
    performer: string;
    required_roles: string[];
    actual_role: string;
    employee_id: string;
    employee_name: string;
    at: string;
  }[];
  queued_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

/** Every state is a column, empty ones included, so the board never moves under the reader. */
export interface TaskBoard {
  columns: Record<string, TaskCard[]>;
  counts: Record<string, number>;
  per_column: number;
}

export interface TaskEvent {
  id: string;
  type: TaskEventType;
  actor_type: 'user' | 'employee' | 'system' | 'github';
  actor_id: string | null;
  message: string | null;
  payload: Record<string, unknown>;
  noteworthy: boolean;
  created_at: string;
}

export interface TaskCost {
  total_micro_usd: number;
  budget_limit_micro_usd: number | null;
  budget_used_pct: number | null;
  run_count: number;
  by_phase: { phase: string; cost_micro_usd: number; runs: number; tokens_in: number; tokens_out: number }[];
  by_model: { model: string; cost_micro_usd: number; runs: number; tokens_in: number; tokens_out: number }[];
}

export interface TaskDependency {
  id: string;
  type: 'blocks' | 'relates';
  blocking: boolean;
  depends_on: { id: string; number: number; title: string; status: TaskStatus; type: TaskType };
  created_at: string;
}

export interface TaskDependencies {
  depends_on: TaskDependency[];
  dependents: TaskDependency[];
  /** Subtasks are ordinary tasks with `parent_task_id` set, so they are cards. */
  subtasks: TaskCard[];
}

/** One stage as `TaskPipeline::describe` spells it; `key` disambiguates a phase that appears twice. */
export interface PipelineStage {
  key: string;
  phase: string;
  performer: string;
  task_status: TaskStatus;
  optional: boolean;
  read_only: boolean;
  needs_sandbox: boolean;
}

export interface TaskPipeline {
  type: string;
  stages: PipelineStage[];
  current_status: TaskStatus;
}

export type JourneyNodeKind = 'milestone' | 'stage' | 'gate';
export type JourneyNodeState = 'pending' | 'skipped' | 'active' | 'done' | 'failed' | 'blocked' | 'cancelled';
export type JourneyEdgeKind = 'traversed' | 'planned' | 'back' | 'side';

/**
 * One node of `GET /tasks/{id}/journey`: a stage of the plan, or one of the
 * fixed points around it (`intake`, `ci`, `merge`, `approval`, `blocked`, `end`),
 * with what the runs and the timeline say about it. No coordinates: the graph is
 * laid out by the client, as the org chart is.
 */
export interface JourneyNode {
  id: string;
  kind: JourneyNodeKind;
  state: JourneyNodeState;
  phase: string | null;
  performer: string | null;
  task_status: TaskStatus | null;
  optional: boolean;
  actor: { id: string; name: string } | null;
  attempts: number;
  /** 1 + the back edges into the node: how many times the work came round to it. */
  rounds: number;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  cost_micro_usd: number;
  outcome: string | null;
  last_run: { id: string; status: string } | null;
}

export interface JourneyEdge {
  id: string;
  source: string;
  target: string;
  kind: JourneyEdgeKind;
  /** On a `back` edge, how many times it was taken. */
  label: string | null;
}

export interface TaskJourney {
  current_status: TaskStatus;
  nodes: JourneyNode[];
  edges: JourneyEdge[];
}

export interface TaskFilters {
  status?: string;
  type?: string;
  priority?: string;
  /** Un uuid, o `none` para las tareas que no tienen proyecto. */
  project_id?: string;
  assignee_employee_id?: string;
  label?: string;
  q?: string;
  /** `'true'`: solo archivadas; `'false'`: solo vivas; ausente: ambas. */
  archived?: string;
  limit?: string;
}

export interface CreateTaskBody {
  project_id?: string | null;
  parent_task_id?: string | null;
  type: TaskType;
  title: string;
  description: string;
  acceptance_criteria?: string | null;
  priority: TaskPriority;
  repository_ids: string[];
  assignee_employee_id?: string | null;
  budget_limit_usd?: number | null;
  labels: string[];
  due_at?: string | null;
  auto_queue: boolean;
  team_meeting: boolean;
  await_deploy: boolean;
}

/** The short form of an employee `GET /tasks/{id}/conversations` names everywhere. */
export interface EmployeeRef {
  employee_id: string;
  slug: string;
  name: string;
}

export type TaskConversationKind = 'ask' | 'delegation' | 'escalation' | 'notification' | 'chat';

/**
 * `answered`/`awaiting`/`unanswered` for an ask or an escalation; `delegated`
 * for a delegation; `delivered` for the rest; `blocked` when nothing got through.
 */
export type TaskConversationOutcome = 'answered' | 'awaiting' | 'unanswered' | 'delegated' | 'delivered' | 'blocked';

/** Who wrote to whom in a thread, with how many times; a refused attempt counts as `blocked`. */
export interface TaskConversationExchange {
  from: EmployeeRef;
  to: EmployeeRef;
  messages: number;
  blocked: number;
}

/** What one run that spoke in the thread did, in its own words (`output_summary`). */
export interface TaskConversationWork {
  employee: EmployeeRef;
  run: {
    id: string;
    phase: string;
    status: string;
    exit_reason: string | null;
    started_at: string | null;
    ended_at: string | null;
    cost_micro_usd: number;
  };
  /** The task the run belongs to — an answer comes from the responder's mailbox task, not from this one. */
  task: { id: string; number: number; title: string };
  output_summary: string | null;
}

/**
 * The summary of one thread — derived from the messages and the runs they
 * name, never written by a model.
 */
export interface TaskConversationSummary {
  opened_by: EmployeeRef | null;
  addressed_to: EmployeeRef[];
  outcome: TaskConversationOutcome;
  answered_by: EmployeeRef | null;
  answered_at: string | null;
  response_ms: number | null;
  response_deadline_at: string | null;
  /** The answer came after the `ask` had timed out: the asker had moved on. */
  late: boolean;
  delegated_task: { id: string; number: number; title: string; status: TaskStatus; assignee: EmployeeRef | null } | null;
  blocked_attempts: number;
  /** Delivered messages. */
  messages: number;
  work: TaskConversationWork[];
}

/**
 * One row of `GET /tasks/{id}/conversations`: a thread of `12 §10` seen from
 * the task, with its summary and its messages (as `GET /conversations/{id}/messages`
 * renders them, blocked attempts included). A thread of a subtask names it in
 * `task`.
 */
export interface TaskConversation {
  id: string;
  topic: string;
  scope: ConversationScope;
  status: ConversationStatus;
  agreement_id: string | null;
  task: { id: string; number: number; title: string } | null;
  kind: TaskConversationKind;
  message_count: number;
  created_at: string;
  last_message_at: string | null;
  participants: ConversationParticipant[];
  exchanges: TaskConversationExchange[];
  summary: TaskConversationSummary;
  messages: Message[];
}

export const tasks = {
  list: (companyId: string, filters: TaskFilters = {}) => {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') params.set(key, value);
    }

    return authFetch<Page<Task>>(`/companies/${companyId}/tasks?${params.toString()}`);
  },
  /** `projectId` es un uuid, o `none` para la pestaña de las tareas sin proyecto (`12 §8`). */
  board: (companyId: string, projectId?: string) =>
    authFetch<TaskBoard>(
      `/companies/${companyId}/tasks/board${projectId ? `?project_id=${projectId}` : ''}`,
    ),
  create: (companyId: string, body: CreateTaskBody) =>
    authFetch<TaskDetail>(`/companies/${companyId}/tasks`, { method: 'POST', body }),
  get: (id: string) => authFetch<TaskDetail>(`/tasks/${id}`),
  update: (id: string, body: Record<string, unknown>) =>
    authFetch<TaskDetail>(`/tasks/${id}`, { method: 'PATCH', body }),
  events: (id: string) => authFetch<{ data: TaskEvent[] }>(`/tasks/${id}/events`),
  runs: (id: string) => authFetch<{ data: RunSummary[] }>(`/tasks/${id}/runs`),
  cost: (id: string) => authFetch<TaskCost>(`/tasks/${id}/cost`),
  dependencies: (id: string) => authFetch<TaskDependencies>(`/tasks/${id}/dependencies`),
  addDependency: (id: string, body: { depends_on_task_id: string; type: 'blocks' | 'relates' }) =>
    authFetch<TaskDependency>(`/tasks/${id}/dependencies`, { method: 'POST', body }),
  pipeline: (id: string) => authFetch<TaskPipeline>(`/tasks/${id}/pipeline`),
  journey: (id: string) => authFetch<TaskJourney>(`/tasks/${id}/journey`),
  conversations: (id: string) => authFetch<{ data: TaskConversation[] }>(`/tasks/${id}/conversations`),

  /**
   * The transitions of `12 §8`. Cancel demands a reason (it destroys paid work);
   * unblock takes an optional note; retry goes back to the **queue**, not to the
   * phase it died in. `assign` with `auto` runs the router of `09 §3.3` and
   * answers 409 with the ranking when nobody scores above zero (case O-08).
   */
  queue: (id: string) => authFetch<Task>(`/tasks/${id}/queue`, { method: 'POST', body: {} }),
  assign: (id: string, body: { employee_id?: string | null; auto?: boolean }) =>
    authFetch<Task>(`/tasks/${id}/assign`, { method: 'POST', body }),
  /**
   * `close_pull_request` travels only when the manager ticked it: the backend
   * ignores it on a task without a PR, but a body that says `false` on a task
   * that never had one reads as a decision nobody took.
   */
  cancel: (id: string, reason: string, closePullRequest = false) =>
    authFetch<Task>(`/tasks/${id}/cancel`, {
      method: 'POST',
      body: closePullRequest ? { reason, close_pull_request: true } : { reason },
    }),
  retry: (id: string, reason?: string) =>
    authFetch<Task>(`/tasks/${id}/retry`, { method: 'POST', body: { reason: reason ?? null } }),
  unblock: (id: string, note?: string) =>
    authFetch<Task>(`/tasks/${id}/unblock`, { method: 'POST', body: { note: note ?? null } }),
  comment: (id: string, body: string) =>
    authFetch<{ id: string; type: string; message: string; created_at: string }>(`/tasks/${id}/comment`, {
      method: 'POST',
      body: { body },
    }),
  /**
   * Una tarea hecha, enviada a otra vuelta con los ajustes del gerente (`12 §8`,
   * `reopen`). El motivo es obligatorio: es el encargo de la siguiente sesión,
   * no una nota para la línea temporal.
   */
  reopen: (id: string, reason: string) =>
    authFetch<Task>(`/tasks/${id}/reopen`, { method: 'POST', body: { reason } }),
  /**
   * La estantería (`12 §8`): solo tareas terminales, 409 para las vivas. No es
   * una transición —el estado no cambia— así que no pide motivo: no destruye
   * nada y se deshace con el botón de al lado.
   */
  archive: (id: string) => authFetch<Task>(`/tasks/${id}/archive`, { method: 'POST', body: {} }),
  unarchive: (id: string) => authFetch<Task>(`/tasks/${id}/unarchive`, { method: 'POST', body: {} }),
  /**
   * La estantería entera de una vez: toda terminal sin archivar de la empresa,
   * acotada al proyecto si el tablero está filtrado. Sin ids a propósito: el
   * tablero capa sus columnas, y mandar «lo visible» archivaría 25 de 140.
   */
  archiveFinished: (companyId: string, projectId?: string) =>
    authFetch<{ archived: number; task_numbers: number[] }>(
      `/companies/${companyId}/tasks/archive-finished${projectId ? `?project_id=${projectId}` : ''}`,
      { method: 'POST', body: {} },
    ),
};

export interface Approval {
  id: string;
  company_id: string;
  subject_type: ApprovalSubjectType;
  subject_id: string;
  title: string;
  summary: string;
  status: ApprovalStatus;
  blocking: boolean;
  bulk_approvable: boolean;
  task: {
    id: string;
    number: number;
    title: string;
    type: TaskType;
    status: TaskStatus;
    risk_level: RiskLevel;
  } | null;
  requested_by: { type: 'user' | 'employee' | 'webhook' | 'schedule'; id: string | null };
  expires_at: string | null;
  created_at: string;
  /** Detail only: everything the decision screen of `09 §6.1` needs. */
  payload?: Record<string, unknown>;
  decision_reason?: string | null;
  decided_by?: Record<string, unknown> | null;
  decided_at?: string | null;
}

export const approvals = {
  /** Everything pending, in every company of the caller — the inbox of `13 §3.8`. */
  inbox: () => authFetch<{ data: Approval[] }>('/users/me/approvals'),
  company: (companyId: string, filters: { status?: ApprovalStatus; subject_type?: string } = {}) => {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') params.set(key, value);
    }

    return authFetch<{ data: Approval[]; pending_count: number }>(
      `/companies/${companyId}/approvals?${params.toString()}`,
    );
  },
  get: (id: string) => authFetch<Approval>(`/approvals/${id}`),
  approve: (id: string, reason?: string) =>
    authFetch<Approval>(`/approvals/${id}/approve`, { method: 'POST', body: { reason: reason ?? null } }),
  /** The reason is mandatory: it is what reaches the agent (`09 §6.3`). */
  reject: (id: string, reason: string) =>
    authFetch<Approval>(`/approvals/${id}/reject`, { method: 'POST', body: { reason } }),
  bulkApprove: (approvalIds: string[], reason?: string) =>
    authFetch<{ approved: Approval[]; skipped: { id: string; reason: string }[] }>('/approvals/bulk-approve', {
      method: 'POST',
      body: { approval_ids: approvalIds, reason: reason ?? null },
    }),
};

/**
 * The `rules` document of `09 §5.1`, exactly as
 * `config/schemas/autonomy-rules.v1.json` requires it: every branch present,
 * because one policy applies whole and nothing merges between scopes.
 */
export interface AutonomyRules {
  plan_gate: {
    enabled: boolean;
    on_task_types: TaskType[];
    on_risk_levels: RiskLevel[];
  };
  auto_merge: {
    enabled: boolean;
    require_ci_green: boolean;
    require_ai_qa_approval: boolean;
    require_peer_review_approvals: number;
    allowed_task_types: TaskType[];
    max_risk_level: RiskLevel;
    max_files_changed: number;
    max_lines_changed: number;
    forbid_if_touches: string[];
  };
  human_gates: {
    on_task_types: TaskType[];
    on_risk_levels: RiskLevel[];
    on_repository_create: boolean;
    on_dependency_change: boolean;
    on_budget_increase: boolean;
    on_first_task_of_project: boolean;
  };
  retry: {
    max_attempts_per_phase: number;
    /** Optional: absent means the platform's own default of five. */
    max_interruptions_per_phase?: number;
    /** Optional: absent means the platform's own default of five (`09 §7.7`). */
    max_phase_continuations?: number;
    max_review_cycles: number;
    max_qa_cycles: number;
    max_ci_fix_cycles: number;
    backoff_seconds: number[];
  };
  escalation: {
    on_repeated_failure: boolean;
    on_budget_threshold: number;
    on_stuck_minutes: number;
  };
}

export interface AutonomyPolicy {
  id: string;
  company_id: string;
  project_id: string | null;
  name: string;
  rules: AutonomyRules;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ResolvedAutonomy {
  scope: 'project' | 'company' | 'system';
  policy_id: string | null;
  name: string | null;
  rules: AutonomyRules;
  is_system_fallback: boolean;
  description: string;
}

export interface GateScenario {
  task_type: TaskType;
  risk_level: RiskLevel;
  ci: 'success' | 'failure' | 'pending' | 'not_required';
  qa_approved: boolean | null;
  approving_reviews: number;
  changed_files: number;
  added_lines: number;
  removed_lines: number;
  changed_paths: string[];
  assignee_may_merge: boolean;
  budget_exhausted: boolean;
}

export interface GateVerdict {
  verdict: 'auto' | 'human' | 'wait';
  reason_code: string;
  summary: string;
  policy: ResolvedAutonomy;
}

export const autonomy = {
  list: (companyId: string) =>
    authFetch<{
      company_policy: AutonomyPolicy | null;
      project_policies: AutonomyPolicy[];
      effective_company: ResolvedAutonomy;
    }>(`/companies/${companyId}/autonomy-policies`),
  /**
   * The body is the rules document itself with `name` and `enabled` as an
   * envelope at the top level — not `{rules: …}`. The backend strips the
   * envelope and validates the rest whole against `autonomy-rules.v1.json`,
   * whose `additionalProperties: false` rejects any other wrapping.
   */
  putCompany: (companyId: string, body: { name?: string; enabled?: boolean; rules: AutonomyRules }) =>
    authFetch<AutonomyPolicy>(`/companies/${companyId}/autonomy-policy`, {
      method: 'PUT',
      body: { ...body.rules, ...(body.name === undefined ? {} : { name: body.name }), enabled: body.enabled ?? true },
    }),
  putProject: (projectId: string, body: { name?: string; enabled?: boolean; rules: AutonomyRules }) =>
    authFetch<AutonomyPolicy>(`/projects/${projectId}/autonomy-policy`, {
      method: 'PUT',
      body: { ...body.rules, ...(body.name === undefined ? {} : { name: body.name }), enabled: body.enabled ?? true },
    }),
  /** Removed; answers with what now takes effect — the company default or the system fallback. */
  deleteProject: (projectId: string) =>
    authFetch<ResolvedAutonomy>(`/projects/${projectId}/autonomy-policy`, { method: 'DELETE' }),
  simulate: (projectId: string, scenario: GateScenario) =>
    authFetch<GateVerdict>(`/projects/${projectId}/autonomy-policy/simulate`, {
      method: 'POST',
      body: scenario,
    }),
};
